import {useState, PropsWithChildren, useRef, useEffect} from 'react';
import {styled} from '@mui/material/styles';
import {collapseClasses, Button, Collapse} from '@mui/material';
import Typography from '@mui/material/Typography';

import {isSafari} from 'utils/deviceInfo';
import {shouldNotForwardProp} from 'utils/styled';

import ArrowDownIcon from 'components/icons/arrowDown.svg';
import ArrowUpIcon from 'components/icons/arrowUp.svg';

/*
  Answer from ChatGPT about Safari and :has() support:
  This is a known Safari quirk.
  Safari (even the latest versions) has delayed evaluation and updates for :has() when the DOM changes dynamically,
  especially when used with :empty or nested selectors.
  Chrome uses a live DOM observer for :has(), but Safari batches those updates — hence the ~1 s delay you’re seeing.
 */
const SSection = styled(
  'div',
  shouldNotForwardProp('hidden')
)<{
  hidden: boolean;
}>(({hidden}) => ({
  ...(isSafari
    ? {
        display: hidden ? 'none' : 'block',
      }
    : {
        // Manually hide empty Collapse, because we dynamically generate its content
        [`&:has(.${collapseClasses.root} > .${collapseClasses.wrapper} > .${collapseClasses.wrapperInner}:empty)`]: {
          display: 'none',
        },
      }),
}));

const SButton = styled(Button)(({theme}) => ({
  ...theme.typography.subtitle2,
  padding: theme.spacing(1, 1.5),
  marginBottom: theme.spacing(2),
}));

export const SectionCollapse = ({
  collapsible = true,
  children,
  title,
}: PropsWithChildren<{
  collapsible?: boolean;
  title: string;
}>) => {
  const [expanded, setExpanded] = useState(true);
  const [hidden, setHidden] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current || !isSafari) {
      return;
    }

    const inner = ref.current.querySelector(`.${collapseClasses.wrapperInner}`);

    if (!inner) {
      throw new Error('Could not find Collapse wrapperInner element');
    }

    setHidden(inner.childNodes.length === 0);

    const observer = new MutationObserver(mutations => {
      const mutation = mutations[0];
      setHidden(mutation.target.childNodes.length === 0);
    });

    observer.observe(inner, {
      childList: true,
      subtree: false,
    });

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  if (!collapsible) {
    return (
      <SSection hidden={hidden}>
        <Typography variant='subtitle2' marginBottom={2} paddingLeft={1.5}>
          {title}
        </Typography>
        <Collapse in={expanded} ref={ref}>
          {children}
        </Collapse>
      </SSection>
    );
  }

  return (
    <SSection hidden={hidden}>
      <SButton
        variant='contained'
        color='info'
        endIcon={expanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
        onClick={() => setExpanded(!expanded)}
      >
        {title}
      </SButton>
      <Collapse in={expanded} ref={ref}>
        {children}
      </Collapse>
    </SSection>
  );
};
